import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  CalendarIcon,
  ArrowUpDown,
  Eye,
  ArrowLeft,
  ExternalLink,
  Clock,
  Loader2,
  CheckCircle2,
  PauseCircle,
  Link2,
  Building2,
  Plus,
  Filter,
  X,
  RotateCcw,
  AlertCircle,
  User
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import RemarksThreadCell from '@/components/RemarksThreadCell';
import OutputLinksCell from '@/components/OutputLinksCell';


// Helper function to safely parse JSON response
const safeJsonParse = async (response) => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text || 'Unknown error' };
  }
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { fetchTaskStatuses, getStatusMeta } from '@/lib/taskStatuses';

export default function BrandWorkspacePage() {
  const { brandName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, user: currentUser } = useAuth();
  const decodedBrandName = decodeURIComponent(brandName);
  const currentEmail = (currentUser?.email || '').toLowerCase();

  // "first.last@wellversed.in" → "First Last" for a friendly Submitted By cell.
  const nameFromEmail = (email) => {
    if (!email) return '—';
    const local = String(email).split('@')[0] || email;
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ') || email;
  };
  
  const [tasks, setTasks] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platforms, setPlatforms] = useState([]);
  // Submitter dropdown — pick anyone; logged-in user pinned at top w/ "me" tag.
  const [submitterFilter, setSubmitterFilter] = useState('all');
  
  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, typesRes, assigneesRes, platformsRes, statusesRes] = await Promise.all([
        fetch(`${API_URL}/api/tasks?brand_name=${encodeURIComponent(decodedBrandName)}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/task-types`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/assignees`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platforms`, { credentials: 'include' }),
        fetchTaskStatuses(true),
      ]);

      if (tasksRes.ok) {
        const fresh = await tasksRes.json();
        setTasks(fresh);
        // Re-sync the currently open detail modal so any edit made in
        // Task Log (or elsewhere) is reflected the moment we regain focus
        // on Brand Workspace. Without this the Eye popover kept showing
        // pre-edit values until a full page reload.
        setSelectedTask(prev => {
          if (!prev) return prev;
          const match = fresh.find(t => t.id === prev.id);
          return match ? { ...prev, ...match } : prev;
        });
      }
      if (typesRes.ok) setTaskTypes(await typesRes.json());
      if (assigneesRes.ok) setAssignees(await assigneesRes.json());
      if (platformsRes.ok) {
        const data = await platformsRes.json();
        setPlatforms(data.map(p => typeof p === 'string' ? p : p.name));
      }
      if (statusesRes) setStatusOptions(statusesRes);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [decodedBrandName]);

  // Auto-refresh on window focus and every 2 minutes (mirrors Task Log) so
  // that switching tabs after editing a task never leaves the Eye popover
  // showing stale values.
  useDataRefresh(fetchData, {
    refreshOnFocus: true,
    refreshInterval: 120000,
    minRefreshGap: 10000,
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-open task detail if URL contains ?task=<task_id>
  useEffect(() => {
    const targetTaskId = searchParams.get('task');
    if (targetTaskId && tasks.length > 0 && !selectedTask) {
      const match = tasks.find(t => t.task_id === targetTaskId || t.id === targetTaskId);
      if (match) {
        setSelectedTask(match);
        // Clean the param from URL to avoid re-opening on refresh
        const next = new URLSearchParams(searchParams);
        next.delete('task');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, tasks, selectedTask, setSearchParams]);

  const filteredTasks = useMemo(() => {
    let result = [...tasks];
    
    // Apply filters
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(task =>
        task.task_id.toLowerCase().includes(searchLower) ||
        task.design_brief.toLowerCase().includes(searchLower) ||
        (task.submitter_email || '').toLowerCase().includes(searchLower)
      );
    }
    if (platformFilter !== 'all') result = result.filter(t => t.platform === platformFilter);
    if (taskTypeFilter !== 'all') result = result.filter(t => t.task_type === taskTypeFilter);
    // Media filter — mirrors Task Log: static / video / content. The
    // "content" bucket falls back to keyword-matching the task-type name so
    // legacy tasks without a task-type record still classify correctly.
    const CONTENT_KEYWORDS = /(blog|script|caption|article|post|copy|email|newsletter|social)/i;
    if (mediaTypeFilter !== 'all') {
      result = result.filter(t => {
        const taskTypeData = taskTypes.find(tt => tt.name === t.task_type);
        if (mediaTypeFilter === 'content') {
          if (taskTypeData?.kind === 'content') return true;
          if (!taskTypeData && CONTENT_KEYWORDS.test(t.task_type || '')) return true;
          return false;
        }
        return taskTypeData?.media_type === mediaTypeFilter;
      });
    }
    if (statusFilter !== 'all') result = result.filter(t => t.status === statusFilter);
    if (submitterFilter && submitterFilter !== 'all') {
      const wanted = submitterFilter.toLowerCase();
      result = result.filter(t => (t.submitter_email || '').toLowerCase() === wanted);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'created_at' || sortField === 'expected_end_date') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    
    return result;
  }, [tasks, taskTypes, search, platformFilter, taskTypeFilter, mediaTypeFilter, statusFilter, submitterFilter, sortField, sortOrder]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTasks.length / pageSize);
  const paginatedTasks = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTasks.slice(start, start + pageSize);
  }, [filteredTasks, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, platformFilter, taskTypeFilter, mediaTypeFilter, statusFilter, submitterFilter, pageSize]);

  const updateTaskAssignee = async (taskId, assigneeId, assigneeName) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/assignee`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ assignee_id: assigneeId === 'unassigned' ? '' : assigneeId, assignee_name: assigneeName })
      });
      
      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update assignee');
      }
      
      const updatedTask = await response.json();
      setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask(updatedTask);
      }
      toast.success('Assignee updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update assignee');
    }
  };

  const updateTaskDetails = async (taskId, field, value) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/details`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });
      
      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update');
      }
      
      const updatedTask = await response.json();
      setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask(updatedTask);
      }
      toast.success('Updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update');
    }
  };

  // Output link state for modal
  const [outputLinkInput, setOutputLinkInput] = useState('');
  const [isAddingOutput, setIsAddingOutput] = useState(false);
  const [isSavingOutput, setIsSavingOutput] = useState(false);

  // Handler used by the shared OutputLinksCell popover — mirrors Task Log so
  // the two lists behave identically (multi-link add/remove, Asset Library
  // shortcut chip).
  const handleOutputUpdate = async (taskId, outputLinks) => {
    try {
      const body = Array.isArray(outputLinks)
        ? { output_links: outputLinks }
        : { output_link: (outputLinks || '').trim() };
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/output`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update output');
      }
      const updatedTask = await response.json();
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updatedTask : t)));
      if (selectedTask?.id === taskId) setSelectedTask(updatedTask);
      toast.success('Output updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update output');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update status');
      }

      const updatedTask = await response.json();
      setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask(updatedTask);
      }
      toast.success(`Status updated to ${getStatusMeta(statusOptions, newStatus).label}`);
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const resolveRework = async (task) => {
    if (!task) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.id}/rework/resolve`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await safeJsonParse(res);
        throw new Error(err.detail || 'Failed to resolve rework');
      }
      const updated = await res.json();
      setTasks(prev => prev.map(t => (t.id === updated.id ? { ...t, ...updated } : t)));
      if (selectedTask?.id === task.id) setSelectedTask(updated);
      toast.success(`Rework marked resolved on ${task.task_id}`);
    } catch (e) {
      toast.error(e.message || 'Failed to resolve rework');
    }
  };

  // Rework metrics for the amber stat strip
  const reworkRequestedCount = useMemo(() => tasks.filter(t => t.rework_requested).length, [tasks]);
  const reworkTotalCount = useMemo(
    () => tasks.reduce((sum, t) => sum + (t.rework_count || 0), 0),
    [tasks]
  );

  const handleSaveOutputLink = async (taskId) => {
    if (!outputLinkInput.trim()) {
      toast.error('Please enter a valid link');
      return;
    }

    setIsSavingOutput(true);
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/output`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ output_link: outputLinkInput.trim() })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save output link');
      }

      setTasks(prev => prev.map(t => t.id === taskId ? data : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask(data);
      }
      setOutputLinkInput('');
      setIsAddingOutput(false);
      toast.success('Output link saved successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to save output link');
    } finally {
      setIsSavingOutput(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return '-';
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const hasActiveFilters = search || platformFilter !== 'all' || taskTypeFilter !== 'all' || mediaTypeFilter !== 'all' || statusFilter !== 'all' || submitterFilter !== 'all';

  // Unique submitters — logged-in user pinned at top.
  const submitters = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const em = (t.submitter_email || '').trim();
      if (!em) continue;
      const key = em.toLowerCase();
      if (!map.has(key)) map.set(key, em);
    }
    const currentEm = (currentUser?.email || '').trim();
    if (currentEm && !map.has(currentEm.toLowerCase())) map.set(currentEm.toLowerCase(), currentEm);
    const list = Array.from(map.values()).sort((a, b) => a.localeCompare(b));
    if (currentEm) {
      const idx = list.findIndex(x => x.toLowerCase() === currentEm.toLowerCase());
      if (idx > 0) {
        const [me] = list.splice(idx, 1);
        list.unshift(me);
      }
    }
    return list;
  }, [tasks, currentUser]);

  const clearFilters = () => {
    setSearch('');
    setPlatformFilter('all');
    setTaskTypeFilter('all');
    setStatusFilter('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#2EB4AC]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="brand-workspace-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2EB4AC]/10 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-[#2EB4AC]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{decodedBrandName} Workspace</h1>
              <p className="text-sm text-slate-500">{tasks.length} tasks</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rework strip — shows when any task in this brand has rework requested */}
      {reworkRequestedCount > 0 && (
        <div
          className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4 p-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50/60"
          data-testid="rework-stat-strip"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-md shadow-amber-500/25 flex-shrink-0">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <p className="text-2xl font-bold text-amber-900 tabular-nums" data-testid="rework-tile-count">
                  {reworkRequestedCount}
                </p>
                <p className="text-sm font-semibold text-amber-800">
                  {reworkRequestedCount === 1 ? 'task' : 'tasks'} flagged for rework
                </p>
                {reworkTotalCount > reworkRequestedCount && (
                  <span className="text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900">
                    {reworkTotalCount} cycles total
                  </span>
                )}
              </div>
              <p className="text-xs text-amber-700/80 mt-0.5">
                Click the amber Rework badge on any row to read the reason and mark it resolved when fixed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters Card */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-slate-500" />
              <CardTitle className="text-lg">Filters</CardTitle>
            </div>
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700"
                data-testid="clear-filters-btn"
              >
                <X className="h-4 w-4 mr-1" />
                Clear all
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by Task ID, Brief, or Email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 border-slate-200"
              data-testid="search-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="platform-filter">
                <SelectValue placeholder="Platform" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {platforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="task-type-filter">
                <SelectValue placeholder="Task Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {taskTypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={mediaTypeFilter} onValueChange={setMediaTypeFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="media-type-filter">
                <SelectValue placeholder="Media" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Media</SelectItem>
                <SelectItem value="static">Static</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="content">Content</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(s => {
                  const meta = getStatusMeta(statusOptions, s.value);
                  return (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <meta.Icon className="h-4 w-4" />
                        {s.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results Count & Page Size */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-slate-500">
            Showing <span className="font-medium text-slate-900">{paginatedTasks.length}</span> of <span className="font-medium text-slate-900">{filteredTasks.length}</span> tasks
          </p>
          <Select value={submitterFilter} onValueChange={setSubmitterFilter}>
            <SelectTrigger className="h-8 w-[220px] text-xs border-slate-200 bg-white" data-testid="submitter-filter">
              <div className="flex items-center gap-1.5 min-w-0">
                <User className="h-3 w-3 text-slate-400 shrink-0" />
                <SelectValue placeholder="All submitters" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All submitters</SelectItem>
              {submitters.map((em) => {
                const isMe = em.toLowerCase() === currentEmail;
                return (
                  <SelectItem key={em} value={em}>
                    <div className="flex items-center gap-2">
                      <span>{nameFromEmail(em)}</span>
                      {isMe && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#2EB4AC]/10 text-[#2EB4AC] border border-[#2EB4AC]/30">
                          me
                        </span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Show:</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="15">15</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tasks Table — reference-style card matching Task Log */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_8px_rgba(15,23,42,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          {paginatedTasks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No tasks found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-white border-b border-slate-100 hover:bg-white">
                  <TableHead 
                    className="cursor-pointer hover:text-slate-700 whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4"
                    onClick={() => toggleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Submitted
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Submitted By</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:text-slate-700 whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4"
                    onClick={() => toggleSort('task_id')}
                  >
                    <div className="flex items-center gap-1">
                      Task ID
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Platform</TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Task Type</TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Media</TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">
                    <div className="flex items-center gap-1">
                      Required By
                    </div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4 border-l border-slate-100">Status</TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Assignee</TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">End Date</TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Output</TableHead>
                  <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Remarks</TableHead>
                  <TableHead className="text-right whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTasks.map((task) => {
                  const statusConfig = getStatusMeta(statusOptions, task.status);
                  const StatusIcon = statusConfig.Icon;
                  return (
                    <TableRow
                      key={task.id}
                      className={cn(
                        "border-b border-slate-100 last:border-b-0 transition-colors",
                        task.rework_requested
                          ? "bg-amber-50/40 hover:bg-amber-50/60"
                          : "hover:bg-slate-50/60"
                      )}
                    >
                      <TableCell className="text-slate-500 text-xs whitespace-nowrap py-4">
                        {format(new Date(task.created_at), "MMM d, HH:mm")}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm py-4" data-testid={`submitted-by-cell-${task.task_id}`}>
                        <div className="flex items-center gap-2.5">
                          {/* Circle initial avatar — same identity system as Task Log */}
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                            style={(() => {
                              const email = (task.submitter_email || '').toLowerCase();
                              let h = 0;
                              for (let i = 0; i < email.length; i++) h = ((h << 5) - h + email.charCodeAt(i)) | 0;
                              const hue = Math.abs(h) % 360;
                              return { backgroundColor: `hsl(${hue} 80% 94%)`, color: `hsl(${hue} 55% 40%)` };
                            })()}
                            title={task.submitter_email}
                          >
                            {(() => {
                              const nm = nameFromEmail(task.submitter_email);
                              const p = nm.split(/\s+/).filter(Boolean);
                              return (p.length === 1 ? p[0].slice(0, 2) : p[0][0] + p[p.length - 1][0]).toUpperCase();
                            })()}
                          </div>
                          <div className="flex flex-col leading-tight min-w-0">
                            <span className="text-slate-900 font-medium">{nameFromEmail(task.submitter_email)}</span>
                            <span className="text-[10px] text-slate-400 truncate max-w-[160px]" title={task.submitter_email}>{task.submitter_email}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-[#2EB4AC] whitespace-nowrap py-4">
                        <div className="flex items-center gap-1.5">
                          {task.task_id}
                          {task.rework_requested && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:bg-amber-200 hover:border-amber-300 transition-colors"
                                  data-testid={`rework-badge-${task.task_id}`}
                                  title="Rework details"
                                >
                                  <RotateCcw className="h-2.5 w-2.5" />
                                  Rework{task.rework_count > 1 ? ` ×${task.rework_count}` : ''}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                side="top"
                                align="start"
                                className="w-[340px] p-4"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                      <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                                    </div>
                                    <p className="text-[11px] font-bold text-amber-700 uppercase tracking-[0.14em]">
                                      Rework Requested
                                    </p>
                                    {task.rework_count > 1 && (
                                      <span className="ml-auto text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                                        ×{task.rework_count}
                                      </span>
                                    )}
                                  </div>

                                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
                                    <p className="text-sm text-slate-800 whitespace-pre-wrap break-words leading-relaxed">
                                      {task.rework_reason || '(no reason provided)'}
                                    </p>
                                  </div>

                                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                    <span className="font-semibold text-slate-600">{task.rework_requested_by || 'unknown'}</span>
                                    {task.rework_requested_at && (
                                      <>
                                        <span className="text-slate-300">·</span>
                                        <span>{format(new Date(task.rework_requested_at), 'MMM d, HH:mm')}</span>
                                      </>
                                    )}
                                  </div>

                                  <div className="pt-1">
                                    {(task.submitter_email || '').toLowerCase() === currentEmail ? (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); resolveRework(task); }}
                                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-500 text-white text-xs font-bold uppercase tracking-[0.14em] hover:bg-emerald-600 transition-colors shadow-sm shadow-emerald-500/20"
                                        data-testid={`resolve-rework-${task.task_id}`}
                                      >
                                        <CheckCircle2 className="h-4 w-4" />
                                        Mark Resolved
                                      </button>
                                    ) : (
                                      <p className="text-[11px] text-slate-400 italic text-center">
                                        Only {task.submitter_email} can mark this resolved.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-slate-100 text-slate-700 text-xs">
                          {task.platform}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[120px] text-xs">
                        <span className="truncate" title={task.task_type}>{task.task_type}</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {(() => {
                          const taskTypeData = taskTypes.find(t => t.name === task.task_type);
                          if (taskTypeData?.media_type === 'video') {
                            return <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">Video</Badge>;
                          }
                          return <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">Static</Badge>;
                        })()}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {task.required_by_date ? format(new Date(task.required_by_date), "MMM d") : '-'}
                      </TableCell>
                      
                      {/* Design Team Editable Columns */}
                      <TableCell className="py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn("h-7 px-2 gap-1 border", statusConfig.pill)}
                              data-testid={`status-dropdown-${task.task_id}`}
                            >
                              <StatusIcon className={cn("h-3 w-3", task.status === 'in_progress' && "animate-spin")} />
                              <span className="text-xs">{statusConfig.label}</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            {statusOptions.map((option) => {
                              const meta = getStatusMeta(statusOptions, option.value);
                              return (
                                <DropdownMenuItem
                                  key={option.value}
                                  onClick={() => handleStatusChange(task.id, option.value)}
                                  className={cn(
                                    "cursor-pointer",
                                    task.status === option.value && "bg-slate-100"
                                  )}
                                >
                                  <meta.Icon className="h-4 w-4 mr-2" />
                                  {option.label}
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={task.assignee_id || 'unassigned'} 
                          onValueChange={(v) => {
                            const assignee = assignees.find(a => a.id === v);
                            updateTaskAssignee(task.id, v, assignee?.name || '');
                          }}
                        >
                          <SelectTrigger className="h-7 w-[100px] text-xs border-dashed">
                            <SelectValue placeholder="Assign" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {assignees.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 text-xs px-2 hover:bg-slate-100">
                              {task.expected_end_date ? format(new Date(task.expected_end_date), "MMM d") : <span className="text-slate-400">Set</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={task.expected_end_date ? new Date(task.expected_end_date) : undefined}
                              onSelect={(date) => updateTaskDetails(task.id, 'expected_end_date', date?.toISOString())}
                            />
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                      <TableCell>
                        <OutputLinksCell task={task} onSave={handleOutputUpdate} />
                      </TableCell>
                      <TableCell className="py-4">
                        <RemarksThreadCell
                          task={task}
                          onThreadUpdate={(taskId, patch) => {
                            setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedTask(task)}
                          className="text-slate-500 hover:text-[#2EB4AC]"
                          data-testid={`view-task-${task.task_id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <p className="text-sm text-slate-500">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      className={currentPage === pageNum ? "bg-[#2EB4AC] hover:bg-[#25908A]" : ""}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Task Detail Modal - Full View like TaskLogPage */}
      <Dialog open={!!selectedTask} onOpenChange={() => { setSelectedTask(null); setIsAddingOutput(false); setOutputLinkInput(''); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-[#2EB4AC]">{selectedTask?.task_id}</span>
              {selectedTask && (() => {
                const statusConfig = getStatusMeta(statusOptions, selectedTask.status);
                const StatusIcon = statusConfig.Icon;
                return (
                  <Badge className={cn("border", statusConfig.pill)}>
                    <StatusIcon className={cn("h-3 w-3 mr-1", selectedTask.status === 'in_progress' && "animate-spin")} />
                    {statusConfig.label}
                  </Badge>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-6 pt-4">
              {/* Status Change */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-500">Change Status:</span>
                <div className="flex gap-2 flex-wrap">
                  {statusOptions.map((option) => {
                    const isActive = selectedTask.status === option.value;
                    const meta = getStatusMeta(statusOptions, option.value);
                    return (
                      <Button
                        key={option.value}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleStatusChange(selectedTask.id, option.value)}
                        className={cn(
                          "h-8",
                          isActive ? "bg-[#2EB4AC] hover:bg-[#2EB4AC]/90" : ""
                        )}
                        data-testid={`modal-status-${option.value}`}
                      >
                        <meta.Icon className={cn("h-3.5 w-3.5 mr-1", option.value === 'in_progress' && isActive && "animate-spin")} />
                        {option.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Brand</p>
                  <p className="font-medium">{selectedTask.brand_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Platform</p>
                  <p className="font-medium">{selectedTask.platform}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Task Type</p>
                  <p className="font-medium">{selectedTask.task_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Dimensions</p>
                  <p className="font-medium">{selectedTask.dimensions || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Priority</p>
                  <p className="font-medium">{selectedTask.priority || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Required By</p>
                  <p className="font-medium">
                    {selectedTask.required_by_date ? format(new Date(selectedTask.required_by_date), "MMMM d, yyyy") : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Submitted At</p>
                  <p className="font-medium">
                    {format(new Date(selectedTask.created_at), "MMMM d, yyyy HH:mm")}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Assignee</p>
                  <Select 
                    value={selectedTask.assignee_id || 'unassigned'} 
                    onValueChange={(v) => {
                      const assignee = assignees.find(a => a.id === v);
                      updateTaskAssignee(selectedTask.id, v, assignee?.name || '');
                    }}
                  >
                    <SelectTrigger className="h-8 w-full mt-1">
                      <SelectValue placeholder="Assign..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {assignees.map((assignee) => (
                        <SelectItem key={assignee.id} value={assignee.id}>
                          {assignee.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Expected End Date</p>
                  <p className="font-medium">
                    {selectedTask.expected_end_date ? format(new Date(selectedTask.expected_end_date), "MMMM d, yyyy") : '—'}
                  </p>
                </div>
              </div>

              {/* Remarks Section — Threaded chat view */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-500">Remarks Thread</p>
                  <span className="text-[10px] text-slate-400">
                    {(selectedTask.remarks_thread || []).length || (selectedTask.remarks ? 1 : 0)} messages
                  </span>
                </div>
                <div className="p-1 bg-slate-50 rounded-lg min-h-[60px] max-h-[220px] overflow-y-auto">
                  <RemarksThreadCell
                    task={selectedTask}
                    onThreadUpdate={(taskId, patch) => {
                      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
                      setSelectedTask((prev) => (prev && prev.id === taskId ? { ...prev, ...patch } : prev));
                    }}
                  />
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Design Brief</p>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="whitespace-pre-wrap">{selectedTask.design_brief}</p>
                </div>
              </div>

              {selectedTask.reference_links?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Reference Links</p>
                  <div className="space-y-2">
                    {selectedTask.reference_links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#2EB4AC] hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {link}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Featured Products with Creative Counts */}
              {selectedTask.featured_products_details?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Featured Products</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.featured_products_details.map((product) => (
                      <Badge 
                        key={product.id} 
                        variant="secondary"
                        className="bg-[#2EB4AC]/10 text-[#2EB4AC] border border-[#2EB4AC]/20"
                      >
                        {product.product_name}
                        {product.creatives_count && (
                          <span className="ml-1.5 px-1.5 py-0.5 bg-[#2EB4AC] text-white text-[10px] rounded-full">
                            ×{product.creatives_count}
                          </span>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Output Link Section */}
              <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100/50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-[#2EB4AC]" />
                    <p className="font-medium text-slate-700">Output / Deliverable</p>
                  </div>
                  {selectedTask.output_link && !isAddingOutput && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setOutputLinkInput(selectedTask.output_link);
                        setIsAddingOutput(true);
                      }}
                      className="text-slate-500 hover:text-[#2EB4AC] text-xs"
                    >
                      Edit
                    </Button>
                  )}
                </div>
                
                {selectedTask.output_link && !isAddingOutput ? (
                  <a
                    href={selectedTask.output_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#2EB4AC] hover:underline break-all"
                  >
                    <ExternalLink className="h-4 w-4 flex-shrink-0" />
                    {selectedTask.output_link}
                  </a>
                ) : isAddingOutput ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm text-slate-600">Output Link</Label>
                      <Input
                        value={outputLinkInput}
                        onChange={(e) => setOutputLinkInput(e.target.value)}
                        placeholder="https://drive.google.com/..."
                        className="mt-1"
                        data-testid="output-link-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSaveOutputLink(selectedTask.id)}
                        disabled={isSavingOutput}
                        className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                        data-testid="save-output-link-btn"
                      >
                        {isSavingOutput ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsAddingOutput(false);
                          setOutputLinkInput('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAddingOutput(true)}
                    className="text-[#2EB4AC] border-[#2EB4AC]/30 hover:bg-[#2EB4AC]/10"
                    data-testid="add-output-link-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Output Link
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Submitter</p>
                  <p className="font-medium">{selectedTask.submitter_email}</p>
                </div>
                {selectedTask.notification_emails?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500">Notification Recipients</p>
                    <p className="font-medium">
                      {selectedTask.notification_emails.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
